import { Button, Stack, Typography } from '@mui/material'
import { EquipmentPlannedDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { HatchCoverFormData } from '@operations/stores/HatchCoverMoveStore'
import { useTranslate } from '@tolgee/react'
import { ContainerMobile, Header, IconButton, MenuIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { VesselTallyStore } from '../../models/vessel-tally-store.model'
import { HatchCoverMoveForm } from '../Molecules/HatchCoverMoveForm'

interface IProps {
  vesselVisitId: number
  selectedGangId?: number
  title: string
  selectedCrane: EquipmentPlannedDto
  openDrawer: () => void
  onRecordHatchCoverMove: () => void
  operationStoppedComponent?: React.ReactNode
  vesselTallyStore: VesselTallyStore
}

export const HatchCoverMove = observer(
  ({
    vesselVisitId,
    selectedGangId,
    title,
    selectedCrane,
    openDrawer,
    onRecordHatchCoverMove,
    operationStoppedComponent,
    vesselTallyStore,
  }: IProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const formId = 'hatch-cover-form'

    const { hatchCoverMoveStore, appStore } = useOperationsStore()

    useEffect(() => {
      const fetchData = async () => {
        await hatchCoverMoveStore.loadRecordsByVisitId(vesselVisitId)
      }

      fetchData()
    }, [hatchCoverMoveStore, vesselVisitId])

    const onSubmit = async (formData: HatchCoverFormData) => {
      await hatchCoverMoveStore.onSubmit(
        { ...formData, equipmentId: selectedCrane.id },
        selectedGangId,
      )

      appStore.setShowAlert(
        'success',
        t('hatchCoverMoveRecordedSuccessfully', 'Hatch cover move recorded successfully'),
      )

      onRecordHatchCoverMove()
    }

    return (
      <>
        <Header
          title={title}
          subTitle={`${t('selectedCrane', 'Selected crane')} - ${selectedCrane.name}`}
          leftRenderOption={
            vesselTallyStore.isSelectedVisitInOperation ? (
              <IconButton onClick={openDrawer}>
                <MenuIcon />
              </IconButton>
            ) : undefined
          }
          rightRenderOption={
            <Button
              variant='contained'
              type='submit'
              form={formId}
              data-cy='record-hatch-cover-move-btn'
              disabled={!vesselTallyStore.isSelectedVisitInOperation}
            >
              {t('record', 'Record')}
            </Button>
          }
        />
        <ContainerMobile sx={{ pt: theme.customSpacing.l }}>
          <Stack gap={theme.customSpacing.m}>
            <Stack alignItems='center'>
              <Typography variant='h1'>
                {hatchCoverMoveStore.totalOpened}/{hatchCoverMoveStore.totalClosed}
              </Typography>
              <Typography variant='body2'>{t('openedClosed', 'Opened/Closed')}</Typography>
            </Stack>

            <HatchCoverMoveForm formId={formId} vesselVisitId={vesselVisitId} onSubmit={onSubmit} />
          </Stack>
          {operationStoppedComponent}
        </ContainerMobile>
      </>
    )
  },
)
